import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../Store';
import { colors, fonts, glyphs, useMediaQuery } from '../../../../Styles';
import { Button, ButtonList, NewInput, NewInputHandles, NewSelect} from '../../../../Components';
import { NewFieldStyles } from '../../../../Definitions';
import { Graph_QL } from "../../../../Services"

// TODO
import Setting from '../../../../Components/Settings/setting';
import timeZoneOptions from '../../../../Assets/timeZones';
import NewTimePicker from '../../../../Components/BaseComponents/newTimePicker';
import { useMilestones } from '../../../../Services/Agrisys_GqlDataCaller/MilestoneDataCalls/useMilestone';

export default function SystemSettings() {
  const { t } = useTranslation()
  const milestoneNameRef = useRef<NewInputHandles>(null);
  const mobileXs = useMediaQuery(`(max-width: 570px)`);
  const responsiveBox = useMediaQuery(`(max-width: 1420px)`);
    
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const {data: milestonesState, call: getMilestones, maintainEntries: maintainMilestones} = useMilestones(connection);
  
  const milestones = useMemo(() => {
    if (milestonesState[domain]) {
      return milestonesState[domain].data
    }
    return []
  }, [domain, milestonesState])

  useEffect(() => {
    getMilestones({fetchAmount: 500}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const [unit, setUnit] = useState<number>(0)

  const unitsOptions = [    
    { label: t("globalSettingsPages.globalSettings.metric", {defaultValue: "metric"}), id: 0 },
    { label: t("globalSettingsPages.globalSettings.imperial", {defaultValue: "imperial"}), id: 1 },
  ]

  return ( <div style={{display: "flex", gap: "20px", alignItems: mobileXs ? "center" : "end", justifyContent: "center", flexDirection: "column", width: !responsiveBox ? "1050px" : "100%"}}>

    {/* Time Zone */}
    <Setting startEnhancer={glyphs.Time({size: "25px"})} header={t("globalSettingsPages.globalSettings.timeZone", {defaultValue: "time zone"})} content={
      <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
        <NewSelect
          options={timeZoneOptions}
          onChange={(e) => setUnit(Number(e?.id ?? 0))}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyTertiary
          }}
          valueOverrides={{
            defaultValue: timeZoneOptions.find(option => option.id === 30)
          }}
          isSearchable
        />
      </div>
    }/>

    {/* Units */}
    <Setting startEnhancer={glyphs.Kg({size: "25px"})} header={t("units.units", {defaultValue: "units"})} content={
      <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
        <NewSelect
          isDisabled
          options={unitsOptions}
          onChange={(e) => setUnit(Number(e?.id ?? 0))}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyTertiary
          }}
          valueOverrides={{
            defaultValue: unitsOptions.find(option => option.id === unit)
          }}
        />
      </div>
    }/>

    {/* Feed Day Start */}
    <Setting startEnhancer={glyphs.TimeToday({size: "25px"})} header={t("globalSettingsPages.globalSettings.feedDayStart", {defaultValue: "feed day start"})} content={
      <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
        <NewTimePicker 
          overrides={{
            fieldStyle: NewFieldStyles.greyLightSecondary
          }}
          background
          isDisabled
        />
      </div>
    }/>

    {/* Milestones */}
    <Setting startEnhancer={glyphs.Setting({size: "25px"})} header={t("globalSettingsPages.globalSettings.milestones", {defaultValue: "milestones"})} content={
      <>    
        <div style={{display: "flex", flexWrap: "wrap", gap: "5px", justifyContent: mobileXs ? "center" : "start"}}>
          <NewInput ref={milestoneNameRef}
            placeholder={t("milestones.general.newMilestone", {defaultValue: "New Milestone"})} 
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary
            }} 
            isClearable
          />
          <Button
              content={t("buttons.add", {defaultValue: "add"})}
              overrides={{
                baseButton: { width:"fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px"},
                contentStyles: {color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4},
                hoverStyles: {backgroundColor: colors.greyLightSecondary}
              }}
              onClick={() => {if (milestoneNameRef.current?.value && maintainMilestones) {maintainMilestones({data: [{milestoneName: milestoneNameRef.current.value}]}); console.log(milestoneNameRef.current.value)}}}
            />
        </div>
        <div style={{height: "2px", backgroundColor: colors.greyLightPrimary, width: "100%", margin: "15px 0"}}/>
        <div style={{display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: mobileXs ? "center" : "start"}}>
          {milestones.filter((milestone) => !milestone.deleted).map((milestone) => (
            <Button 
              content={milestone.name} 
              endEnhancer={glyphs.Close({size: "10px"})}
              onClick={() => {if (maintainMilestones) {
                maintainMilestones({data: [{...milestone, deleted: true}]})
              }}} 
              key={`milestone:${milestone.milestone_id}`}
              overrides={{
                baseButton: {width: "fit-content", height: "fit-content", padding: "4px 6px", backgroundColor: colors.greyPrimary},
                hoverStyles: {backgroundColor: colors.greySecondary},
                endEnhancerStyles: {marginLeft: "5px"}
              }}
            />
          ))}
        </div>
      </>
    } />

    {/* Save/discard btns */}
    <ButtonList 
      contents={[t("globalSettingsPages.globalSettings.saveChanges", {defaultValue: "save changes"}), t("globalSettingsPages.globalSettings.discardChanges", {defaultValue: "discard changes"})]} 
      onClick={(i => {
        if (i === 0) {}
        else if (i === 1) {}
      })}
      overrides={{
        baseButton: {width: "fit-content", height: "fit-content", padding: "10px", backgroundColor: colors.greyPrimary},
        hoverStyles: {backgroundColor: colors.greySecondary},
        endEnhancerStyles: {marginLeft: "5px"}
      }}      
    />
  </div> );
}